import React from "react";
import { Checkbox } from "@altimateai/lego";
import { RunMetadata, FilterOptionsResponse } from "../../services/adeBench";
import { RunRow } from "./RunRow";
import { FilterableHeader } from "./FilterableHeader";
import { SortHeader, SortColumn, SortDirection } from "./SortHeader";

interface RunsTableProps {
  filteredRuns: RunMetadata[];
  expandedRunId: string | null;
  selectedRunIds: Set<string>;
  filterOptions: FilterOptionsResponse | undefined;
  selectedAgents: string[];
  selectedModels: string[];
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  allSelected: boolean;
  onRunClick: (runId: string) => void;
  onRunSelect: (runId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onAgentsChange: (vals: string[]) => void;
  onModelsChange: (vals: string[]) => void;
  onSort: (column: SortColumn) => void;
}

export const RunsTable: React.FC<RunsTableProps> = ({
  filteredRuns,
  expandedRunId,
  selectedRunIds,
  filterOptions,
  selectedAgents,
  selectedModels,
  sortColumn,
  sortDirection,
  allSelected,
  onRunClick,
  onRunSelect,
  onSelectAll,
  onAgentsChange,
  onModelsChange,
  onSort,
}) => {
  return (
    <div className="al-bg-white al-rounded-lg al-border al-overflow-hidden">
      <table className="al-w-full">
        <thead className="al-bg-gray-100">
          <tr>
            <th className="al-p-3 al-text-center al-w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked: boolean | "indeterminate") =>
                  onSelectAll(checked as boolean)
                }
              />
            </th>
            <th className="al-p-3 al-text-left al-text-sm al-font-medium al-w-8"></th>
            <SortHeader
              column="start_time"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Timestamp
            </SortHeader>
            <th className="al-p-3 al-text-left al-text-sm al-font-medium">Run ID</th>
            <FilterableHeader
              options={(filterOptions?.agents || []).map(a => ({
                value: a,
                label: a,
              }))}
              selectedValues={selectedAgents}
              onSelectionChange={onAgentsChange}
            >
              Agent
            </FilterableHeader>
            <FilterableHeader
              options={(filterOptions?.models || []).map(m => ({
                value: m,
                label: m,
              }))}
              selectedValues={selectedModels}
              onSelectionChange={onModelsChange}
            >
              Model
            </FilterableHeader>
            <SortHeader
              column="accuracy"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Accuracy
            </SortHeader>
            <SortHeader
              column="dataset_size"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              Tasks
            </SortHeader>
            <th className="al-p-3 al-text-left al-text-sm al-font-medium">Pass@k</th>
            <th className="al-p-3 al-text-left al-text-sm al-font-medium">User</th>
          </tr>
        </thead>
        <tbody>
          {filteredRuns.map(run => (
            <RunRow
              key={run.run_id}
              run={run}
              isExpanded={expandedRunId === run.run_id}
              isSelected={selectedRunIds.has(run.run_id)}
              onRunClick={onRunClick}
              onRunSelect={onRunSelect}
            />
          ))}
        </tbody>
      </table>

      {filteredRuns.length === 0 && (
        <div className="al-p-8 al-text-center al-text-gray-500">No benchmark runs found</div>
      )}
    </div>
  );
};
